import { UnreachableCaseError } from 'ts-essentials';
import {
  assertValidGeovectorRegionAndEnvironment,
  type GeovectorClientEnvironment,
  type GeovectorRegion,
} from '@trimble/utilities';
import { createClient, type Client } from '../generated/client';
import { getAuthToken } from '../generated/core/auth.gen';

export type { Client as MapsClient };

export type ServiceType = 'maps' | 'cloud';

const BEARER_SECURITY = { scheme: 'bearer', type: 'http' } as const;

export async function applyMapsServiceAuth(
  request: Request,
  client: Client
): Promise<Request> {
  if (!request.headers.has('Authorization')) {
    const token = await getAuthToken(BEARER_SECURITY, client.getConfig().auth);
    if (token) {
      request.headers.set('Authorization', token);
    }
  }
  return request;
}

export interface MapsClientOptions {
  env: GeovectorClientEnvironment;
  region: GeovectorRegion;
  service?: ServiceType;
  baseUrl?: string;
}

const getMapsServiceBaseUrl = ({
  env,
  region,
  service = 'cloud',
}: MapsClientOptions): string => {
  assertValidGeovectorRegionAndEnvironment(env, region);

  const envSubdomain = env === 'prod' ? '' : `${env}.`;
  const cloudSubdomain =
    env === 'prod' ? 'api.trimble.com' : 'stage.api.trimblecloud.com';

  switch (service) {
    case 'cloud':
      return `https://cloud.${cloudSubdomain}/Map-Services/maps-${region}/1.0/`;

    case 'maps':
      return `https://${region}.${envSubdomain}api.maps.trimblegeospatial.com`;

    default:
      throw new UnreachableCaseError(service);
  }
};

export const createMapsServiceClient = ({
  env,
  region,
  service = 'cloud',
  baseUrl: baseUrlOverride,
}: MapsClientOptions): Client => {
  const baseUrl =
    baseUrlOverride ?? getMapsServiceBaseUrl({ env, region, service });

  const client = createClient({ baseUrl });
  client.interceptors.request.use((request) =>
    applyMapsServiceAuth(request, client)
  );
  return client;
};
